import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  CalendarDays,
  CheckCircle2,
  Clock3,
  CreditCard,
  Download,
  ExternalLink,
  FileAudio,
  Loader2,
  MessageSquareText,
  RefreshCw,
  Send,
  Star,
  UploadCloud,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLocale } from "../context/LocaleContext";
import { api, API, fmtPrice } from "../lib/api";

const labels = {
  pending_engineer: "Menunggu jawaban engineer",
  awaiting_payment: "Menunggu pembayaran",
  paid: "Pembayaran diterima",
  in_progress: "Sedang dikerjakan",
  preview_delivered: "Preview tersedia",
  revision_requested: "Revisi diminta",
  completed: "Selesai",
  cancelled: "Dibatalkan",
  rejected: "Ditolak engineer",
};

const styles = {
  pending_engineer: "text-amber-300 bg-amber-400/10 border-amber-400/20",
  awaiting_payment: "text-amber-300 bg-amber-400/10 border-amber-400/20",
  paid: "text-sky-300 bg-sky-400/10 border-sky-400/20",
  in_progress: "text-violet-300 bg-violet-400/10 border-violet-400/20",
  preview_delivered: "text-[#fb7185] bg-[#e11d48]/10 border-[#e11d48]/20",
  revision_requested: "text-orange-300 bg-orange-400/10 border-orange-400/20",
  completed: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20",
  cancelled: "text-zinc-500 bg-white/5 border-white/10",
  rejected: "text-zinc-400 bg-white/5 border-white/10",
};

export default function CustomerServiceOrders() {
  const { loading: authLoading, isCustomer } = useAuth();
  const { locale } = useLocale();
  const localeTag = locale === "en" ? "en-US" : "id-ID";
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [revision, setRevision] = useState("");
  const [workspace, setWorkspace] = useState(null);
  const [message, setMessage] = useState("");
  const [projectFile, setProjectFile] = useState(null);
  const [ratingDraft, setRatingDraft] = useState({ orderId: "", value: 0 });
  const [ratingHover, setRatingHover] = useState(0);

  const selected = orders.find((order) => order.id === selectedId) || orders[0];
  const selectedRating = ratingDraft.orderId === selected?.id ? ratingDraft.value : 0;
  const selectedFiles = workspace && selected && workspace.order?.id === selected.id && Array.isArray(workspace.files)
    ? workspace.files : [];
  const secureDeliveries = selectedFiles.filter(
    (item) => item.uploader_role === "engineer" && ["preview", "final"].includes(item.kind)
  );
  const revisionRequests = selected?.revision_requests || [];
  const latestRevisionAt = revisionRequests.length ? revisionRequests[revisionRequests.length - 1].at : "";
  const finalDeliveryReady = secureDeliveries.some(
    (item) => item.kind === "final" && (!latestRevisionAt || item.created_at > latestRevisionAt)
  );

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer/service-orders");
      setOrders(data);
      setSelectedId((current) => current || data[0]?.id || "");
    } catch (error) {
      toast.error(error.userMessage || "Pesanan gagal dimuat");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isCustomer) loadOrders(); }, [isCustomer, loadOrders]);
  useEffect(() => {
    if (!isCustomer || !selectedId) return;
    setWorkspace(null);
    api.get(`/customer/service-orders/${selectedId}/workspace`).then(({ data }) => setWorkspace(data)).catch(() => setWorkspace(null));
  }, [isCustomer, selectedId]);

  if (authLoading) return <div className="pt-40 text-center text-zinc-500">Loading...</div>;
  if (!isCustomer) return <Navigate to="/login" state={{ from: "/dashboard/services" }} replace />;

  const pay = async () => {
    setAction("pay");
    try {
      const { data } = await api.post(`/service-orders/${selected.id}/checkout/doku`);
      window.location.assign(data.url);
    } catch (error) {
      toast.error(error.userMessage || "Pembayaran belum tersedia");
      setAction("");
    }
  };

  const approve = async () => {
    const confirmation = selected.settlement_mode === "doku_split"
      ? "Setujui hasil final? Dana akan dilepas dan dibagikan melalui DOKU."
      : "Setujui hasil final? Pesanan akan selesai dan admin akan membayar engineer melalui transfer manual.";
    if (!window.confirm(confirmation)) return;
    setAction("approve");
    try {
      const { data } = await api.post(`/service-orders/${selected.id}/approve`);
      setOrders((current) => current.map((item) => item.id === data.id ? data : item));
      toast.success(selected.settlement_mode === "doku_split" ? "Hasil disetujui dan settlement sedang diproses" : "Hasil disetujui; payout engineer masuk antrean admin");
    } catch (error) {
      toast.error(error.userMessage || "Hasil belum dapat disetujui");
    } finally {
      setAction("");
    }
  };

  const submitRating = async () => {
    if (!selected || !selectedRating || action) return;
    setAction("rating");
    try {
      const { data } = await api.post(`/customer/service-orders/${selected.id}/rating`, { rating: selectedRating });
      setOrders((current) => current.map((item) => item.id === data.id ? data : item));
      setRatingDraft({ orderId: "", value: 0 });
      toast.success("Terima kasih, penilaian Anda sudah tersimpan");
    } catch (error) {
      toast.error(error.userMessage || "Penilaian belum dapat dikirim");
    } finally {
      setAction("");
    }
  };

  const requestRevision = async () => {
    if (revision.trim().length < 5) {
      toast.error("Jelaskan revisi yang diperlukan");
      return;
    }
    setAction("revision");
    try {
      const { data } = await api.post(`/service-orders/${selected.id}/revision`, { message: revision });
      setOrders((current) => current.map((item) => item.id === data.id ? data : item));
      setRevision("");
      toast.success("Permintaan revisi dikirim");
    } catch (error) {
      toast.error(error.userMessage || "Revisi gagal dikirim");
    } finally {
      setAction("");
    }
  };

  const refreshWorkspace = async () => {
    const { data } = await api.get(`/customer/service-orders/${selected.id}/workspace`);
    setWorkspace(data);
  };

  const refreshOrdersAndWorkspace = async () => {
    await loadOrders();
    if (selected) {
      try { await refreshWorkspace(); }
      catch (error) { toast.error(error.userMessage || "Hasil project gagal dimuat"); }
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!message.trim()) return;
    setAction("message");
    try { await api.post(`/customer/service-orders/${selected.id}/messages`, { message }); setMessage(""); await refreshWorkspace(); }
    catch (error) { toast.error(error.userMessage || "Pesan gagal dikirim"); }
    finally { setAction(""); }
  };

  const uploadProjectFile = async () => {
    if (!projectFile) return toast.error("Pilih file terlebih dahulu");
    setAction("upload");
    const payload = new FormData(); payload.append("kind", "source"); payload.append("upload", projectFile);
    try { await api.post(`/customer/service-orders/${selected.id}/files`, payload, { headers: { "Content-Type": "multipart/form-data" }, timeout: 0 }); setProjectFile(null); toast.success("File project tersimpan secara privat"); await refreshWorkspace(); }
    catch (error) { toast.error(error.userMessage || "Upload gagal"); }
    finally { setAction(""); }
  };

  const downloadProjectFile = async (item) => {
    try {
      const response = await fetch(`${API}/customer/service-orders/${selected.id}/files/${item.id}/download`, { headers: { Authorization: `Bearer ${localStorage.getItem("ts_token")}` } });
      if (!response.ok) throw new Error();
      const blob = await response.blob(); const url = URL.createObjectURL(blob); const anchor = document.createElement("a"); anchor.href = url; anchor.download = item.filename; anchor.click(); URL.revokeObjectURL(url);
    } catch { toast.error("File gagal diunduh"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-10">
        <div>
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white mb-5"><ArrowLeft className="w-3.5 h-3.5" /> Dashboard</Link>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#e11d48] mb-2">Audio projects</div>
          <h1 className="font-[Outfit] text-4xl md:text-5xl font-black">Pesanan layanan</h1>
        </div>
        <button onClick={refreshOrdersAndWorkspace} className="px-4 py-2.5 rounded-full border border-white/10 hover:bg-white/5 text-xs font-semibold flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5" /> Perbarui</button>
      </div>

      {loading ? <div className="py-32 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#e11d48]" /></div> : orders.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-white/10 py-24 text-center">
          <FileAudio className="w-9 h-9 text-zinc-700 mx-auto mb-4" />
          <h2 className="font-[Outfit] text-2xl font-bold">Belum ada project</h2>
          <p className="text-sm text-zinc-500 mt-2">Pilih engineer dan paket yang sesuai untuk memulai.</p>
          <Link to="/engineers" className="inline-flex mt-6 px-5 py-3 rounded-full bg-[#e11d48] font-semibold text-sm">Cari engineer</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[360px_1fr] gap-6 items-start">
          <aside className="rounded-2xl border border-white/10 bg-[#0a0a0c] p-2 lg:sticky lg:top-24">
            {orders.map((order) => (
              <button key={order.id} onClick={() => { setSelectedId(order.id); setRatingHover(0); }} className={`w-full text-left rounded-xl p-4 transition-colors ${selected?.id === order.id ? "bg-white/10" : "hover:bg-white/5"}`}>
                <div className="flex items-start justify-between gap-3"><div className="font-semibold text-sm truncate">{order.project_title}</div><span className="font-[Outfit] font-bold text-xs whitespace-nowrap">{fmtPrice(order.amount)}</span></div>
                <div className="text-[10px] text-zinc-500 mt-1">{order.engineer_name} · {order.service_name}</div>
                <span className={`inline-flex mt-3 px-2 py-1 rounded-full border text-[9px] font-bold uppercase tracking-wider ${styles[order.status]}`}>{labels[order.status]}</span>
              </button>
            ))}
          </aside>

          {selected && <main className="space-y-6">
            <section className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-6 md:p-8">
              <div className="flex flex-wrap items-start justify-between gap-5">
                <div><div className="font-mono text-[10px] text-zinc-600 mb-2">{selected.order_number}</div><h2 className="font-[Outfit] text-3xl font-bold">{selected.project_title}</h2><p className="text-sm text-zinc-400 mt-2">{selected.engineer_name} · {selected.service_name}</p></div>
                <span className={`inline-flex px-3 py-1.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${styles[selected.status]}`}>{labels[selected.status]}</span>
              </div>
              {selected.audio_track_count && <div className="text-xs text-zinc-400 mt-5">Jumlah track audio project: <span className="font-semibold text-white">{selected.audio_track_count}</span>{selected.service_snapshot?.max_audio_tracks ? ` dari maksimal ${selected.service_snapshot.max_audio_tracks} track` : ""}</div>}
              <div className="grid sm:grid-cols-3 gap-4 mt-7 pt-6 border-t border-white/10">
                <div><div className="text-[10px] text-zinc-600 mb-1">Nilai project</div><div className="font-[Outfit] text-xl font-bold">{fmtPrice(selected.amount)}</div></div>
                <div><div className="text-[10px] text-zinc-600 mb-1">Pengerjaan</div><div className="text-sm font-semibold">{selected.service_snapshot?.turnaround_days} hari</div></div>
                <div><div className="text-[10px] text-zinc-600 mb-1">Revisi</div><div className="text-sm font-semibold">{selected.service_snapshot?.revisions} kali</div></div>
              </div>
              {selected.status === "pending_engineer" && <p className="mt-7 rounded-xl border border-amber-400/20 bg-amber-400/5 p-4 text-sm text-amber-200">Pengajuan sudah dikirim. Pembayaran akan tersedia setelah engineer menerimanya.</p>}
              {selected.status === "rejected" && <p className="mt-7 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-zinc-300">Engineer menolak pengajuan ini.{selected.decision_note && <span className="block mt-2">Alasan: {selected.decision_note}</span>}</p>}
              {selected.status === "awaiting_payment" && <><p className="mt-7 text-sm text-emerald-300">Engineer menerima pengajuan Anda. Silakan lanjutkan pembayaran melalui DOKU. Chat dengan engineer akan aktif setelah pembayaran dikonfirmasi.</p><button onClick={pay} disabled={action === "pay"} className="w-full mt-3 py-3.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] font-semibold flex justify-center items-center gap-2">{action === "pay" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Bayar melalui DOKU</button></>}
            </section>

            {selected.status === "completed" && <section className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-6 md:p-8">
              <h3 className="font-[Outfit] text-xl font-bold">Nilai pekerjaan engineer</h3>
              <p className="text-sm text-zinc-500 mt-2">Beri rating 1–5 bintang untuk hasil project yang sudah selesai. Satu pesanan dapat dinilai satu kali.</p>
              {selected.rating ? <div className="mt-5"><div className="flex items-center gap-1" aria-label={`Penilaian Anda ${selected.rating} dari 5 bintang`}>{[1, 2, 3, 4, 5].map((score) => <Star key={score} className={`w-7 h-7 ${score <= selected.rating ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} aria-hidden="true" />)}</div><p className="text-xs text-emerald-300 mt-3">Terima kasih, Anda sudah menilai pesanan ini.</p></div> : <div className="mt-5"><div className="flex items-center gap-1" role="group" aria-label="Pilih rating engineer">{[1, 2, 3, 4, 5].map((score) => <button key={score} type="button" onClick={() => setRatingDraft({ orderId: selected.id, value: score })} onMouseEnter={() => setRatingHover(score)} onMouseLeave={() => setRatingHover(0)} onFocus={() => setRatingHover(score)} onBlur={() => setRatingHover(0)} aria-label={`${score} dari 5 bintang`} aria-pressed={selectedRating === score} className="p-1 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"><Star className={`w-8 h-8 transition-colors ${score <= (ratingHover || selectedRating) ? "text-amber-400 fill-amber-400" : "text-zinc-600"}`} aria-hidden="true" /></button>)}</div><button type="button" onClick={submitRating} disabled={!selectedRating || !!action} className="mt-5 px-6 py-3 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-black text-sm font-semibold flex items-center gap-2">{action === "rating" && <Loader2 className="w-4 h-4 animate-spin" />} Kirim penilaian</button></div>}
            </section>}

            {selected.payment_status === "paid" && workspace && <section className="grid md:grid-cols-2 gap-5">
              <div className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-6">
                <h3 className="font-[Outfit] text-xl font-bold mb-4">File project privat</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">{workspace.files.map((item) => <button key={item.id} onClick={() => downloadProjectFile(item)} disabled={!!item.deleted_at} className="w-full flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-white/5 text-left disabled:opacity-50"><FileAudio className="w-4 h-4 text-[#fb7185]" /><div className="min-w-0 flex-1"><div className="text-xs truncate">{item.filename}</div><div className="text-[9px] uppercase text-zinc-600 mt-1">{item.kind} · {item.uploader_name}{item.deleted_at ? " · Masa simpan berakhir" : item.engineer_received_at ? " · Diterima engineer" : ""}</div></div>{!item.deleted_at && <Download className="w-4 h-4 text-zinc-600" />}</button>)}</div>
                <p className="mt-3 text-xs text-zinc-500">File sumber yang sudah dikonfirmasi engineer akan dihapus 30 hari setelah project selesai. Simpan salinan Anda sendiri.</p>
                <div className="mt-4 pt-4 border-t border-white/10 flex gap-2"><label className="flex-1 min-w-0 px-3 py-3 rounded-xl border border-dashed border-white/15 text-xs text-zinc-500 truncate cursor-pointer"><input type="file" accept=".wav,.mp3,.flac,.aif,.aiff,.m4a,.ogg,.zip" className="hidden" onChange={(event) => setProjectFile(event.target.files?.[0] || null)} />{projectFile?.name || "Pilih stems/audio/ZIP"}</label><button onClick={uploadProjectFile} disabled={action === "upload"} className="w-11 rounded-xl bg-[#e11d48] flex items-center justify-center">{action === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}</button></div>
              </div>
              <div className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-6 flex flex-col min-h-[360px]"><h3 className="font-[Outfit] text-xl font-bold mb-4 flex items-center gap-2"><MessageSquareText className="w-5 h-5 text-[#e11d48]" /> Percakapan project</h3><div className="flex-1 space-y-3 max-h-60 overflow-y-auto">{workspace.messages.map((item) => <div key={item.id} className={`p-3 rounded-xl text-xs ${item.sender_role === "customer" ? "bg-[#e11d48]/10 ml-8" : "bg-white/5 mr-8"}`}><div data-no-translate className="font-semibold mb-1">{item.sender_name}</div><div data-no-translate className="text-zinc-300 whitespace-pre-wrap">{item.message}</div><div className="text-[9px] text-zinc-600 mt-2">{new Date(item.created_at).toLocaleString(localeTag)}</div></div>)}</div><form onSubmit={sendMessage} className="mt-4 flex gap-2"><input value={message} onChange={(event) => setMessage(event.target.value)} className="flex-1 min-w-0 bg-black border border-white/10 rounded-xl px-3 text-xs focus:outline-none focus:border-[#e11d48]" placeholder="Tulis pesan untuk engineer..." /><button className="w-11 h-11 rounded-xl bg-white text-black flex items-center justify-center"><Send className="w-4 h-4" /></button></form></div>
            </section>}

            {(selected.delivery_links?.length > 0 || secureDeliveries.length > 0) && <section className="rounded-3xl border border-[#e11d48]/30 bg-[#e11d48]/5 p-6 md:p-8">
              <div className="flex items-center gap-2 text-[#fb7185] mb-4"><FileAudio className="w-5 h-5" /><h3 className="font-[Outfit] text-xl font-bold text-white">Hasil tersedia</h3></div>
              <div className="space-y-2">{(selected.delivery_links || []).map((link, index) => <a key={link} href={link} target="_blank" rel="noreferrer" className="flex items-center justify-between px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-sm hover:border-white/20"><span>Preview / hasil {index + 1}</span><ExternalLink className="w-4 h-4 text-zinc-500" /></a>)}</div>
              {selected.status === "preview_delivered" && <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div><textarea value={revision} onChange={(event) => setRevision(event.target.value)} className="w-full min-h-24 bg-black/40 border border-white/10 rounded-xl p-3 text-sm focus:outline-none focus:border-orange-400/50" placeholder="Jelaskan bagian yang perlu direvisi..." /><button onClick={requestRevision} disabled={action === "revision"} className="w-full mt-2 py-3 rounded-full border border-orange-400/30 text-orange-300 text-sm font-semibold flex justify-center items-center gap-2"><MessageSquareText className="w-4 h-4" /> Minta revisi</button></div>
                <div className="flex flex-col gap-2"><button onClick={approve} disabled={action === "approve" || !finalDeliveryReady} className="flex-1 min-h-24 rounded-2xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold flex flex-col items-center justify-center gap-2">{action === "approve" ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-6 h-6" />} {selected.settlement_mode === "doku_split" ? "Setujui final & lepaskan dana" : "Setujui hasil final"}</button>{!finalDeliveryReady && <p className="text-xs text-amber-300">File final terbaru belum tersedia. Klik Perbarui setelah engineer mengunggahnya.</p>}</div>
              </div>}
            </section>}

            <section className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-6 md:p-8">
              <h3 className="font-[Outfit] text-xl font-bold mb-6">Timeline</h3>
              <div className="space-y-0">{(selected.timeline || []).slice().reverse().map((entry, index) => <div key={`${entry.at}-${index}`} className="flex gap-4 pb-6 last:pb-0"><div className="flex flex-col items-center"><div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">{index === 0 ? <BadgeCheck className="w-4 h-4 text-[#e11d48]" /> : <Clock3 className="w-3.5 h-3.5 text-zinc-600" />}</div>{index < (selected.timeline || []).length - 1 && <div className="w-px flex-1 bg-white/10 mt-2" />}</div><div><div className="text-sm font-semibold">{entry.label}</div><div className="text-[10px] text-zinc-600 mt-1 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {new Date(entry.at).toLocaleString(localeTag)}</div></div></div>)}</div>
            </section>
          </main>}
        </div>
      )}
    </div>
  );
}
